import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import LogoutButton from '../components/LogoutButton';
import { getAuthToken } from '../utils/authToken';
import { fetchWithRetry } from '../utils/fetchWithRetry';

export default function ProfilPage() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [email, setEmail] = useState('');
  const [profilingActive, setProfilingActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Load user profile
  useEffect(() => {
    loadUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserProfile = async () => {
    setIsLoading(true);
    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/getUserProfile`, {
        headers: {
          'X-Auth-Token': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDisplayName(data.user.displayName || '');
        setOriginalName(data.user.displayName || '');
        setEmail(data.user.email);
        setProfilingActive(data.user.profilingConsent?.isActive ?? false);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing
      setDisplayName(originalName);
      setIsEditing(false);
    } else {
      // Start editing
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (!displayName.trim() || displayName.trim().length < 2) {
      setSaveMessage('❌ Meno musí mať aspoň 2 znaky');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const token = await getAuthToken(instance);
      if (!token) {
        setSaveMessage('❌ Chyba autentifikácie');
        setIsSaving(false);
        return;
      }

      const response = await fetchWithRetry(`${config.apiUrl}/updateUserProfile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `Bearer ${token}`
        },
        body: JSON.stringify({ displayName: displayName.trim() })
      });

      if (response.ok) {
        setSaveMessage('✓ Uložené');
        setOriginalName(displayName.trim());
        setIsEditing(false);

        // Reload page after 1 second to update header
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const error = await response.json();
        setSaveMessage(`❌ ${error.error || 'Nepodarilo sa uložiť'}`);
      }
    } catch (error) {
      console.error('Failed to save:', error);
      setSaveMessage('❌ Chyba pri ukladaní');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'SmartBloček',
      text: 'Vyskúšaj SmartBloček - inteligentné spracovanie bločkov!',
      url: 'https://smartblocek.sk'
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        alert('Odkaz skopírovaný do schránky!');
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 20px 100px 20px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', marginBottom: '24px' }}>
          Profil
        </h1>
        <div style={{
          background: '#f9fafb',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Meno</div>
            <div style={{ height: '20px', background: '#e5e7eb', borderRadius: '4px', width: '60%', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Email</div>
            <div style={{ height: '20px', background: '#e5e7eb', borderRadius: '4px', width: '80%', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
        </div>
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>
            Nastavenia
          </h2>
          <div style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ height: '16px', background: '#e5e7eb', borderRadius: '4px', width: '70%', marginBottom: '8px', animation: 'pulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: '12px', background: '#e5e7eb', borderRadius: '4px', width: '90%', animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 20px 100px 20px' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', marginBottom: '24px' }}>
        Profil
      </h1>

      {/* User info */}
      {account && (
        <div style={{
          background: '#f9fafb',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          border: '1px solid #e5e7eb',
          position: 'relative'
        }}>
          {/* Edit/Cancel icon */}
          <button
            onClick={handleEditToggle}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '4px'
            }}
            title={isEditing ? 'Zrušiť' : 'Upraviť'}
          >
            {isEditing ? '✕' : '✏️'}
          </button>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Meno</div>
            {isEditing ? (
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#111827',
                  border: '2px solid #667eea',
                  borderRadius: '6px',
                  padding: '8px',
                  width: 'calc(100% - 60px)',
                  outline: 'none'
                }}
                maxLength={100}
              />
            ) : (
              <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                {displayName || 'Používateľ'}
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Email</div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
              {email}
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
              Emailovú adresu nie je možné zmeniť.
            </div>
          </div>

          {/* Save/Cancel buttons when editing */}
          {isEditing && (
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSave}
                disabled={isSaving || displayName.trim() === originalName}
                style={{
                  background: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: isSaving || displayName.trim() === originalName ? 'not-allowed' : 'pointer',
                  opacity: isSaving || displayName.trim() === originalName ? 0.5 : 1
                }}
              >
                {isSaving ? 'Ukladám...' : 'Uložiť'}
              </button>
              <button
                onClick={handleEditToggle}
                disabled={isSaving}
                style={{
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: isSaving ? 'not-allowed' : 'pointer'
                }}
              >
                Zrušiť
              </button>
            </div>
          )}

          {/* Save message */}
          {saveMessage && (
            <div style={{ marginTop: '12px', fontSize: '14px', color: saveMessage.startsWith('✓') ? '#059669' : '#dc2626' }}>
              {saveMessage}
            </div>
          )}
        </div>
      )}

      {/* Settings sections */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#111827', marginBottom: '16px' }}>
          Nastavenia
        </h2>
        <div style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <SettingItem
            emoji="📬"
            label="Profilovací súhlas"
            description="Marketingové e-maily, mesačné prehľady a personalizácia."
            status={profilingActive ? 'Aktívny' : 'Neaktívny'}
            onClick={() => navigate('/profile/marketing-emails')}
          />
          <SettingItem
            emoji="👥"
            label="Moje skupiny"
            description="Spravuj svoje skupiny a ľudí na rozdeľovanie"
            onClick={() => navigate('/profile/moje-skupiny')}
          />
          <SettingItem
            emoji="📁"
            label="Tvoje dáta"
            description="Čo môžeš urobiť so svojimi dátami v SmartBločku"
            onClick={() => navigate('/profile/vase-data')}
          />
          <SettingItem
            emoji="📋"
            label="Právne informácie"
            description="Podmienky, ochrana údajov a licencie"
            onClick={() => navigate('/profile/legal-documents')}
          />
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button
          onClick={handleShare}
          style={{
            background: '#667eea',
            color: '#fff',
            border: 'none',
            borderRadius: '12px',
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📤 Zdieľať SmartBloček
        </button>
        <LogoutButton />
      </div>
    </div>
  );
}

interface SettingItemProps {
  emoji?: string;
  label: string;
  description: string;
  status?: string;
  disabled?: boolean;
  onClick?: () => void;
}

function SettingItem({ emoji, label, description, status, disabled, onClick }: SettingItemProps) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      style={{
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s',
        background: disabled ? 'transparent' : 'transparent'
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = '#f9fafb';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '15px', fontWeight: 500, color: '#111827', marginBottom: '2px' }}>
          {emoji && <span style={{ marginRight: '6px' }}>{emoji}</span>}
          {label}
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          {description}
        </div>
        {status && (
          <div style={{ fontSize: '12px', color: '#667eea', marginTop: '4px', fontWeight: 500 }}>
            {status}
          </div>
        )}
      </div>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
        <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
